import clsx from 'clsx';
import React from 'react';
import AddIcon from '../../assets/icons/add-icon.svg?react';
import Input from '../Input';
import Button from '../Button';
import Select from '../Select';
import EditIcon from '../../assets/icons/edit-icon.svg?react';
import WarningIcon from '../../assets/icons/warning.svg?react';
import {
  GetAllInvestigationQuestionCategoryOfOrganizationResponse,
  InvestigationQuestion,
  InvestigationQuestionTypeEnum,
} from '@EHSNavigator/backend';
import { useButtonSizeSmall } from '../../utils/buttonSizes';
import TagsInput from '../TagsInput';
import { toggleModal } from '../../reducers/modalToggle';
import { useDispatch } from 'react-redux';

interface AddNewQuestionModalProps {
  otherClasses?: string;
  formHandler: any;
  handleClose: () => void;
  allInvestigationQuestionCategories: GetAllInvestigationQuestionCategoryOfOrganizationResponse;
  updateQuestion?: boolean;
  selectedQuestion: InvestigationQuestion | null;
}

const AddNewQuestionModal: React.FC<AddNewQuestionModalProps> = ({
  otherClasses,
  formHandler,
  handleClose,
  allInvestigationQuestionCategories,
  updateQuestion,
  selectedQuestion,
}) => {
  const addNewQuestionModalClasses = clsx(otherClasses);
  const md = useButtonSizeSmall();
  const dispatch = useDispatch();

  const questionCategories =
    allInvestigationQuestionCategories?.investigationQuestionCategories?.map(
      (item) => ({
        label: item?.name,
        value: item?.id,
      }),
    );

  let updatedQustionCategories = [...questionCategories];
  const exists = questionCategories?.some(
    (node) => node.value === formHandler?.values?.questionCategory,
  );

  if (!exists && selectedQuestion?.investigationQuestionCategory?.id) {
    updatedQustionCategories.push({
      label: selectedQuestion?.investigationQuestionCategory?.name || '',
      value: selectedQuestion?.investigationQuestionCategory?.id || '',
    });
  }

  return (
    <div
      className={addNewQuestionModalClasses}
      data-testid="add-new-question-modal"
    >
      <div className="mb-8 flex w-full flex-col items-center gap-2.5 text-center lg:mb-12">
        {updateQuestion ? (
          <EditIcon className="[&>path]:fill-brand-byzantine h-12 w-12 md:h-85 md:w-85" />
        ) : (
          <AddIcon className="[&>path]:fill-brand-byzantine h-12 w-12 md:h-85 md:w-85" />
        )}
        <h4 className="text-brand-byzantine">
          {updateQuestion ? 'Update Question' : 'Create New Question'}
        </h4>
      </div>
      <form action="#" onSubmit={formHandler?.handleSubmit} className="mt-12">
        <Input
          name="question"
          value={formHandler?.values.question}
          onChange={formHandler?.handleChange}
          placeholder="Type question here..."
          type="text"
          errorMessage={
            formHandler?.touched.question && formHandler?.errors.question
              ? formHandler?.errors.question
              : ''
          }
          label="Question"
          onBlur={formHandler?.handleBlur}
          disabled={formHandler?.isSubmitting}
          otherClasses={{ container: 'w-full ' }}
        />

        <Select
          list={Object.values(InvestigationQuestionTypeEnum)?.map((item) => ({
            label: item,
            value: item,
          }))}
          value={formHandler?.values.questionType}
          onSelect={(value) => {
            formHandler?.setFieldValue('questionType', value);
            formHandler?.setFieldValue('options', []);
          }}
          disabled={formHandler?.isSubmitting}
          errorMessage={
            formHandler?.touched.questionType &&
            formHandler?.errors.questionType
              ? formHandler?.errors.questionType
              : ''
          }
          label="Question Type"
          placeholder="Select question type"
          otherClasses={{ container: 'w-full mt-8' }}
        />
        <div className="mt-8 flex flex-col gap-2">
          <button
            type="button"
            className="text-form-label -tracking-form-label text-neutral-tones-text font-inter-variable relative flex w-full items-center gap-1 font-bold md:w-fit"
          >
            {updatedQustionCategories?.length === 0 ? (
              <span className="">
                Question Category{' '}
                <button
                  type="button"
                  className="group inline-block md:relative"
                >
                  <WarningIcon className="h-5 w-5 translate-y-1" />
                  <p className="bg-neutral-tones-sidebar text-neutral-tones-text absolute bottom-full left-2/4 z-10 hidden w-full -translate-x-2/4 rounded px-2 py-1 text-center text-xs font-normal shadow-lg group-hover:block group-focus:block md:min-w-235">
                    To create a category, go to the{' '}
                    <strong className="font-semibold">Manage Categories</strong>{' '}
                    button or simply{' '}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        formHandler?.resetForm();
                        dispatch(toggleModal('manage-custom-categories'));
                      }}
                      className="text-brand-byzantine cursor-pointer"
                    >
                      click here
                    </button>
                    .
                  </p>
                </button>
              </span>
            ) : (
              'Question Category'
            )}
          </button>
          <Select
            list={updatedQustionCategories}
            value={formHandler?.values.questionCategory}
            onSelect={(value) =>
              formHandler?.setFieldValue('questionCategory', value)
            }
            disabled={formHandler?.isSubmitting}
            errorMessage={
              formHandler?.touched.questionCategory &&
              formHandler?.errors.questionCategory
                ? formHandler?.errors.questionCategory
                : ''
            }
            placeholder="Select question category"
            otherClasses={{
              container: 'w-full mt-0',
              listContainer: 'bottom-18 top-auto',
            }}
          />
        </div>

        {formHandler?.values?.questionType ===
          InvestigationQuestionTypeEnum?.MultiOptions && (
          <TagsInput
            disabled={formHandler?.isSubmitting}
            value={formHandler?.values.options}
            onChange={(e) => formHandler?.setFieldValue('options', e, true)}
            errorMessage={
              formHandler?.touched.options && formHandler?.errors.options
                ? formHandler?.errors.options
                : ''
            }
            label={
              <>
                Options
                <button
                  type="button"
                  className="group inline-block md:relative"
                >
                  <WarningIcon className="h-5 w-5" />
                  <p className="bg-neutral-tones-sidebar text-neutral-tones-text absolute bottom-full left-2/4 z-10 hidden w-full -translate-x-2/4 rounded px-2 py-1 text-center text-xs font-normal shadow-lg group-hover:block group-focus:block md:min-w-211">
                    Type and click enter to add your first option.
                  </p>
                </button>
              </>
            }
            placeholder="Type here options..."
            otherClasses={{
              container: 'w-full mt-8',
              label: 'flex items-center gap-1 relative w-full md:w-fit',
            }}
            name="options"
            handleBlur={formHandler?.handleBlur}
          />
        )}

        <div className="mt-8 flex flex-col-reverse gap-15 md:grid md:grid-cols-2 md:gap-6 lg:mt-12">
          <Button
            variant="secondary"
            type="button"
            size={md}
            onClick={() => {
              formHandler.resetForm();
              handleClose();
            }}
            disable={formHandler?.isSubmitting}
            otherClasses="text-brand-posh-pink hover:border-brand-posh-pink hover:text-brand-posh-pink"
          >
            Cancel
          </Button>
          <Button
            loading={formHandler?.isSubmitting}
            disable={
              formHandler?.values?.question ===
                formHandler?.initialValues?.question &&
              formHandler?.values?.questionType ===
                formHandler?.initialValues?.questionType &&
              formHandler?.values?.questionCategory ===
                formHandler?.initialValues?.questionCategory &&
              formHandler?.values?.options ===
                formHandler?.initialValues?.options
            }
            type="submit"
            size={md}
          >
            {updateQuestion ? 'Update' : 'Create'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AddNewQuestionModal;
